import { Box, Modal, useTheme } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import type { NurseFullRole } from "../../../../utils/models";
import { portalOverlayProps } from "../../../UIToolkit/Popups/portalOverlay";
import type { MessageFiltersType, MessageWorkingFilterEnum } from "./MessageFilter";
import { NewBroadcastMessage } from "./NewBroadcastMessage";

export const getShiftPickupMessage = (date?: DateTime, start?: DateTime, end?: DateTime, smsPickup = false) =>
	smsPickup
		? `Reply YES to pickup ${date?.toFormat("ccc, MMM d")}, ${start?.toFormat("h:mma")}\u2013${end?.toFormat("h:mma")} shift.`
		: `Hi,\nWould you like to pick up the ${date?.toFormat("ccc, MMM d")} (${start?.toFormat("h:mma")} - ${end?.toFormat("h:mma")}) shift?\nSubmit your request in the In-House app.`;

export type NewBroadcastMessageModalProps = {
	/** `didSend` is `true` only when a message was actually sent — a discard or a back-out passes nothing. */
	onClose: (didSend?: boolean) => void;
	nurseIdsToIgnore?: number[];
	initialRolesToSelect?: NurseFullRole[];
	initialFilters?: MessageFiltersType[];
	defaultWorkingFilter?: MessageWorkingFilterEnum;
	initialSmsMessage?: string;
	addAppLink?: boolean;
	initialSelectedNurseIds?: number[];
	lockRecipientSelection?: boolean;
	/** Seeds the Jobs recipient filter (job management only); when active it replaces role matching, mirroring backend pickup eligibility. */
	jobId?: string;
	/** Offer "All of <building>": one text to everyone active in the building. Only the general composer offers it, never one opened for a shift. */
	allowFacilityScope?: boolean;
};

export const NewBroadcastMessageModal: FC<NewBroadcastMessageModalProps> = (props) => {
	const theme = useTheme();
	// Dialogs/menus/pickers rendered below portal to <body> and default their z-index off
	// theme.zIndex.modal. This modal pins itself to 1302 so anything nested here that stayed on the
	// unmodified theme default (1300) would render underneath the modal itself. Raising it locally
	// covers every default-tier child — including ones added later — without threading a zIndex prop
	// through each one individually.
	const elevatedTheme = useMemo(() => createTheme(theme, { zIndex: { modal: 1303 } }), [theme]);

	return (
		// No `onClose`: the backdrop deliberately does not close a composer, and Escape is handled inside
		// `NewBroadcastMessage` instead — only the composer knows whether the draft is empty (close outright)
		// or typed ("Discard message?"), and MUI would swallow the key here. This used to pass `toggleBackWarning` from a SECOND
		// `useBroadcastMessageState` instance created here, but the dialog renders from `NewBroadcastMessage`'s
		// own instance, so the flag was never read: the confirm could not appear and the whole duplicate state
		// machine — the recipient list rebuilt over every nurse — was computed twice to feed a dead handler.
		<Modal
			open={true}
			data-testid="broadcast-message-modal"
			{...portalOverlayProps("broadcast-message-modal")}
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				zIndex: 1302 /* sit above popovers pinned to 1301, e.g. the Punch Action Center */,
			}}
		>
			<ThemeProvider theme={elevatedTheme}>
				<Box
					sx={{
						width: "50vw",
					}}
				>
					<NewBroadcastMessage
						onClose={props.onClose}
						nurseIdsToIgnore={props.nurseIdsToIgnore}
						initialRolesToSelect={props.initialRolesToSelect}
						initialFilters={props.initialFilters}
						defaultWorkingFilter={props.defaultWorkingFilter}
						initialSmsMessage={props.initialSmsMessage}
						addAppLink={props.addAppLink}
						initialSelectedNurseIds={props.initialSelectedNurseIds}
						lockRecipientSelection={props.lockRecipientSelection}
						jobId={props.jobId}
						allowFacilityScope={props.allowFacilityScope}
					/>
				</Box>
			</ThemeProvider>
		</Modal>
	);
};
